import { useCallback, useRef, useState } from "react"
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import UploadStep from "components/upload/components/upload-step"
import FileItem from "components/upload/components/file-item"
import { SKILL_UPLOAD_CONFIG } from "components/upload/constants"
import { useNotifications } from "hooks/notifications"
import { log } from "helpers/logger"
import { SkillPackageTooLargeError, normalizeSkillBundle } from "connected-components/agent-chat/chatbox/skill-package-contents"
import { ConfirmActionDialog } from "connected-components/agent-chat/chatbox/confirm-action-dialog"
import type { AddSkillFailure } from "resources/skill-proposals"
import { useAddSkillProposal, useValidateSkillUpload } from "resources/skill-proposals"
import type { SkillValidationResult } from "connected-components/agent-chat/chatbox/skill-proposal-contract"

const INVALID_TYPE_MESSAGE = "is not a .zip or .skill file. Only .zip and .skill are accepted."
const TOO_LARGE_MESSAGE = `exceeds the ${SKILL_UPLOAD_CONFIG.maxSize / (1024 * 1024)} MB limit for a single upload.`
const UNCOMPRESSED_TOO_LARGE_MESSAGE = `unpacks to more than ${SKILL_UPLOAD_CONFIG.maxUncompressedTotalBytes / (1024 * 1024)} MB.`
const TOO_MANY_FILES_MESSAGE = "Only one skill package can be uploaded at a time."
const UPLOAD_STEP_MESSAGES = {
  invalidTypeMessage: INVALID_TYPE_MESSAGE,
  tooLargeMessage: TOO_LARGE_MESSAGE,
  tooManyFilesMessage: TOO_MANY_FILES_MESSAGE,
  formatHint: <>You can only upload a .zip or .skill file.</>,
}

const PREVIEW_SX = { display: "flex", flexDirection: "column", gap: 1 } satisfies SxProps<Theme>

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      const commaIndex = typeof result === "string" ? result.indexOf(",") : -1
      if (typeof result !== "string" || commaIndex === -1) {
        reject(new Error("could not read the selected file"))
        return
      }
      resolve(result.slice(commaIndex + 1))
    }
    reader.onerror = () => reject(reader.error ?? new Error("could not read the selected file"))
    reader.readAsDataURL(file)
  })
}

function FailureAlert({ failure, onRetry }: { failure: AddSkillFailure, onRetry?: () => void }) {
  const retryAction = onRetry && <Button size="small" onClick={onRetry} data-testid="kelly-skill-upload-retry">Try again</Button>
  if (failure.kind === "nameTaken") {
    return <Alert severity="warning" data-testid="kelly-skill-upload-name-taken" action={retryAction}>{failure.message ?? "A skill with this name already exists."}</Alert>
  }
  if (failure.kind === "rejected") {
    return <Alert severity="warning" data-testid="kelly-skill-upload-rejected">{failure.message ?? "This package cannot be added as it stands."}</Alert>
  }
  if (failure.kind === "replaceTargetGone") {
    return <Alert severity="warning" data-testid="kelly-skill-upload-target-gone" action={retryAction}>{failure.message ?? "The skill this would have replaced no longer exists."}</Alert>
  }
  if (failure.kind === "tooLarge") {
    return <Alert severity="warning" data-testid="kelly-skill-upload-too-large">This package is too large to add.</Alert>
  }
  return (
    <Alert severity="error" data-testid="kelly-skill-upload-error" action={retryAction}>
      Something went wrong. Try again.
    </Alert>
  )
}

export interface UploadSkillDialogProps {
  open: boolean
  onClose: () => void
}

export function UploadSkillDialog({ open, onClose }: UploadSkillDialogProps) {
  const notifications = useNotifications()
  const { validate, isValidating, failure: validateFailure, reset: resetValidate } = useValidateSkillUpload()
  const { add, isAdding, failure: addFailure, reset: resetAdd } = useAddSkillProposal()
  const [file, setFile] = useState<File | null>(null)
  const [bundle, setBundle] = useState<string | null>(null)
  const [result, setResult] = useState<SkillValidationResult | null>(null)
  const [localError, setLocalError] = useState<{ kind: "size" | "unreadable", message: string } | null>(null)
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false)
  const generationRef = useRef(0)

  const reset = useCallback(() => {
    generationRef.current += 1
    setFile(null)
    setBundle(null)
    setResult(null)
    setLocalError(null)
    setIsReplaceDialogOpen(false)
    resetAdd()
    resetValidate()
  }, [resetAdd, resetValidate])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const runValidate = useCallback(async (candidateBundle: string, generation: number) => {
    const validated = await validate(candidateBundle)
    if (generationRef.current !== generation) return
    if (validated !== null) setResult(validated)
  }, [validate])

  const addFiles = useCallback((picked: File[]) => {
    const next = picked[0]
    if (next === undefined) return
    generationRef.current += 1
    const generation = generationRef.current
    setLocalError(null)
    setResult(null)
    setFile(next)
    void (async () => {
      try {
        const raw = await readFileAsBase64(next)
        const normalized = await normalizeSkillBundle(raw, SKILL_UPLOAD_CONFIG.maxUncompressedTotalBytes, SKILL_UPLOAD_CONFIG.maxSize)
        if (generationRef.current !== generation) return
        setBundle(normalized)
        void runValidate(normalized, generation)
      } catch (error) {
        if (generationRef.current !== generation) return
        log(error, { component: "UploadSkillDialog", stage: "addFiles" })
        setFile(null)
        if (error instanceof SkillPackageTooLargeError) {
          const message = error.kind === "compressed" ? TOO_LARGE_MESSAGE : UNCOMPRESSED_TOO_LARGE_MESSAGE
          setLocalError({ kind: "size", message: `${next.name} ${message}` })
          return
        }
        setLocalError({ kind: "unreadable", message: `${next.name} could not be read as a skill package.` })
      }
    })()
  }, [runValidate])

  const runAdd = useCallback((replacesSkillId: string | null) => {
    if (bundle === null) return
    setIsReplaceDialogOpen(false)
    void (async () => {
      const added = await add(replacesSkillId === null ? { bundle } : { bundle, replacesSkillId })
      if (added !== null) {
        notifications.success(`"${added.name}" was ${replacesSkillId !== null ? "replaced" : "created"}. It can take up to 5 minutes to reach Kelly.`)
        handleClose()
      }
    })()
  }, [bundle, add, notifications, handleClose])

  const onPrimaryAction = useCallback(() => {
    if (result?.mode === "replace") setIsReplaceDialogOpen(true)
    else runAdd(null)
  }, [result, runAdd])

  const replacesSkillId = result?.mode === "replace" ? result.replacesSkillId : null
  const saveResult = useCallback(() => runAdd(replacesSkillId), [runAdd, replacesSkillId])
  const closeReplaceDialog = useCallback(() => setIsReplaceDialogOpen(false), [])
  const retryValidate = useCallback(() => { if (bundle !== null) void runValidate(bundle, generationRef.current) }, [bundle, runValidate])
  const revalidateAfterStaleFailure = useCallback(() => {
    setResult(null)
    resetAdd()
    if (bundle !== null) void runValidate(bundle, generationRef.current)
  }, [bundle, runValidate, resetAdd])

  const isBusy = isValidating || isAdding

  return (
    <Dialog open={open} onClose={isAdding ? undefined : handleClose} fullWidth maxWidth="sm" data-testid="kelly-skill-upload-dialog">
      <DialogTitle>Upload a skill</DialogTitle>
      <DialogContent>
        {isBusy && <LinearProgress data-testid="kelly-skill-upload-progress" />}
        {localError !== null && (
          <Alert severity="warning" data-testid={localError.kind === "size" ? "kelly-skill-upload-size-error" : "kelly-skill-upload-unreadable"}>
            {localError.message}
          </Alert>
        )}
        {file === null ? (
          <UploadStep
            files={[]}
            addFiles={addFiles}
            removeFile={() => undefined}
            addNotification={notifications.add}
            uploadConfig={SKILL_UPLOAD_CONFIG}
            maxFiles={1}
            type="skill"
            messages={UPLOAD_STEP_MESSAGES}
            legend={null}
          />
        ) : (
          <Box sx={PREVIEW_SX}>
            <List disablePadding><FileItem file={{ file }} divider={false} removeFile={reset} disabled={isAdding} /></List>
            {result !== null && (
              <Typography variant="body2" data-testid="kelly-skill-upload-preview">
                {result.mode === "replace" ? `Replaces "${result.name}"` : `Creates "${result.name}"`}
              </Typography>
            )}
            {result === null && validateFailure !== null && (
              <FailureAlert failure={validateFailure} onRetry={validateFailure.kind === "retryable" ? retryValidate : undefined} />
            )}
            {addFailure !== null && (
              <FailureAlert
                failure={addFailure}
                onRetry={
                  addFailure.kind === "retryable" ? saveResult
                    : addFailure.kind === "nameTaken" || addFailure.kind === "replaceTargetGone" ? revalidateAfterStaleFailure
                      : undefined
                }
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isAdding}>Cancel</Button>
        {result !== null && (
          <Button variant="contained" disabled={isBusy} onClick={onPrimaryAction} data-testid="kelly-skill-upload-confirm">
            {result.mode === "replace" ? "Review and replace" : "Add skill"}
          </Button>
        )}
      </DialogActions>
      <ConfirmActionDialog
        open={isReplaceDialogOpen}
        title={`Replace "${result?.name ?? ""}"?`}
        body={`You already have a skill called "${result?.name ?? ""}". Confirming replaces its contents with this version. Whether it is switched on for Kelly stays as you have it.`}
        confirmLabel="Replace"
        onClose={closeReplaceDialog}
        onConfirm={saveResult}
      />
    </Dialog>
  )
}
